import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/Button";
import { useToastStore, type ToastItem, type ToastKind } from "@/stores/toastStore";

/** Auto-dismiss delays — errors linger longer so they can be read. */
const DISMISS_MS: Record<ToastKind, number> = {
  success: 3000,
  info: 5000,
  error: 6000,
};

/**
 * Hard lifetime cap, hover-paused or not (#833 regression guard): a toast that
 * renders under a STATIONARY pointer receives mouseenter without any real user
 * intent, and with an uncapped hover-pause it would never dismiss — which also
 * livelocks automation (each click retry on an element under the toast
 * re-fires mouseenter, and the dismiss button keeps intercepting the click).
 */
const MAX_TOAST_LIFETIME_MS = 15_000;

/**
 * Whether the pointer can actually hover. Touch devices have no hover concept
 * — a synthetic mouseenter there (emulated events, or a toast appearing under
 * the last tap point) must never pause auto-dismiss.
 * @returns True when the primary pointer supports hover
 */
function canHover(): boolean {
  return (
    typeof window.matchMedia === "function" && window.matchMedia("(hover: hover)").matches
  );
}

/**
 * Per-kind card + dismiss-button styling.
 *
 * The dismiss button signals hover with its background rather than a darker
 * text stop: 700 is the darkest grade the success and destructive ladders
 * define, so a "darker on hover" text rule would resolve to the same colour it
 * started from and the button would read as inert.
 */
const KIND_STYLES: Record<ToastKind, { card: string; button: string }> = {
  success: {
    card: "border-success-500 bg-success-50 text-success-700",
    button:
      "text-success-700 hover:bg-success-100 focus-visible:ring-success-500 focus-visible:ring-offset-success-50",
  },
  error: {
    card: "border-destructive-500 bg-destructive-50 text-destructive-700",
    button:
      "text-destructive-700 hover:bg-destructive-100 focus-visible:ring-destructive-500 focus-visible:ring-offset-destructive-50",
  },
  info: {
    card: "border-primary-500 bg-primary-50 text-primary-800",
    button:
      "text-primary-800 hover:bg-primary-100 focus-visible:ring-primary-500 focus-visible:ring-offset-primary-50",
  },
};

/**
 * A single toast row. Carries its own live-region semantics (`role="status"` +
 * `aria-live="polite"` for success/info, `role="alert"` + `aria-live="assertive"`
 * for errors) so assistive tech announces it on insert. Auto-dismisses after a
 * kind-dependent delay — paused while hovered — and is manually dismissible.
 * No entry/exit motion is used, so `prefers-reduced-motion` needs no
 * special-casing.
 * @param root0 - Props
 * @param root0.toast - The toast to render
 * @returns The toast element
 */
function ToastRow({ toast }: { toast: ToastItem }): React.ReactElement {
  const removeToast = useToastStore((s) => s.removeToast);
  const isError = toast.kind === "error";
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setTimeout(() => {
      removeToast(toast.id);
    }, DISMISS_MS[toast.kind]);
    return () => {
      clearTimeout(timer);
    };
  }, [toast.id, toast.kind, paused, removeToast]);

  // Hard cap — runs from mount and is deliberately NOT cleared by the hover
  // pause, so no toast can become immortal (#833).
  useEffect(() => {
    const cap = setTimeout(() => {
      removeToast(toast.id);
    }, MAX_TOAST_LIFETIME_MS);
    return () => {
      clearTimeout(cap);
    };
  }, [toast.id, removeToast]);

  const styles = KIND_STYLES[toast.kind];

  return (
    <div
      role={isError ? "alert" : "status"}
      aria-live={isError ? "assertive" : "polite"}
      onMouseEnter={() => {
        if (canHover()) setPaused(true);
      }}
      onMouseLeave={() => {
        setPaused(false);
      }}
      className={`pointer-events-auto flex w-full items-start gap-2 rounded-lg border p-3 text-sm font-medium shadow-lg ${styles.card}`}
    >
      <span className="flex-1 pt-0.5">{toast.message}</span>
      <Button
        variant="ghost"
        size="sm"
        aria-label="Dismiss notification"
        onClick={() => {
          removeToast(toast.id);
        }}
        className={`-m-1 shrink-0 ${styles.button}`}
      >
        <X className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden="true" />
      </Button>
    </div>
  );
}

/**
 * App-level toast outlet for the admin app (#562). Mount once near the app
 * root; renders the active toasts in a fixed, bottom-anchored stack
 * (full-width on mobile, right-aligned card on ≥sm), capped at the store's
 * MAX_TOASTS (oldest drops first).
 *
 * Raise toasts with the `toast` helper from `@/stores/toastStore` (e.g. a
 * mutation `onSuccess`/`onError`). The container is pointer-transparent so it
 * never blocks clicks; only the toast cards capture pointer events.
 * @returns The toast outlet (renders nothing when empty)
 */
export function Toaster(): React.ReactElement | null {
  const toasts = useToastStore((s) => s.toasts);
  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed inset-x-4 bottom-4 z-[60] flex flex-col items-stretch gap-2 sm:left-auto sm:right-4 sm:max-w-sm">
      {toasts.map((toast) => (
        <ToastRow key={toast.id} toast={toast} />
      ))}
    </div>
  );
}
